import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Icon } from 'antd';

import TextBlockWithQuillRenderer from '../ComplexDocumentDesign/TextBlockWithQuillRenderer';
import { constructQuillJSON, constructAutoSaveJSON, convertContractToReact } from '../../utils/TextBlockHelper';
import { withTranslation } from 'react-i18next';

import { COMPLEX_DOCUMENT_CONTEXT_KEYS, useDocumentContext } from '../../context/complexDocumentContext';
import { CONTEXT_KEYS, useArtboardContext } from '../../context/complexArtboardContext';

const NAMESPACE = ['EMAIL_SUBJECT_MODAL']
const DEFAULT_VALUE = [
    {
      contentType: 'PARAGRAPH',
      content: ''
    },
    {
      contentType: "LINE_BREAK",
      content: "\n"
    }
  ];

const AccessibilitySummaryEditor = (props) => {
  const { itemUUID, t, contents, customClassName, disabled = false, customContainerStyle = {}, blockID = '' } = props;
  const contentsRef = useRef({ contents });

  const customVarSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.GLOBAL_VARIABLES], []);
  const [globalVariables, store] = useDocumentContext(
    customVarSelector,
    `AccessibilitySummaryEditor`,
  );

  const styleSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.STYLES], []);
  const [documentStyle] = useDocumentContext(styleSelector, 'AccessibilitySummaryEditor');

  const isOpenDesignSelector = useCallback((store) => store[CONTEXT_KEYS.IS_OPEN_DESIGN], []);
  const [isOpenDesign, updateArtboard] = useArtboardContext(isOpenDesignSelector, `AccessibilitySummaryEditor`);

  const blockDetails = store.get()[blockID];

  const [isClearing,setIsClearing] = useState(false);
  const [isSaving,setIsSaving] = useState(false);
  const [editable, setEditable] = useState(false);
  const [textEditable, setTextEditable] = useState(false);
  const [randomKey, setRandomKey] = useState(0);

  useEffect(() => {
    setEditable(false);
    setTextEditable(false);
  }, [itemUUID]);

  const updateSummaryContents = useCallback((contents, undoDiff, redoDiff, cb, ref, op = undefined, newGlobalVariables = []) => {
    const newContents = constructAutoSaveJSON(contents, documentStyle?.fontFamily, documentStyle?.fontSize, newGlobalVariables);
    contentsRef.current = Object.assign({}, contentsRef.current, {
      contents: newContents,
      newGlobalVariables
    });
  }, [documentStyle]);

  const dummyCallback = useCallback(() => {}, []);

  const customBlurHandler = useCallback(() => {
    setTimeout(() => {
      setEditable(isEditable => {
        if (isEditable) {
          setTextEditable(false);
          return !isEditable;
        }
        return isEditable;
      });
    }, 10)
  }, []);

  const onSingleClick = useCallback(() => {
    if (isOpenDesign) {
      updateArtboard({
        [CONTEXT_KEYS.SHOW_SIGNUP_PROMPT]: true
      });
      return;
    }
    if (disabled) return;
    setEditable(e => {
      if (!e) return !e;
      return e;
    });
  }, [disabled]);

  const defaultContent = constructQuillJSON(contents?.length > 0  ? contents : DEFAULT_VALUE, documentStyle?.fontFamily, documentStyle?.fontSize, globalVariables);

  const onSaveChanges = useCallback((type) => {
    type === 'clear' ? setIsClearing(true) : setIsSaving(true);
    if (type === 'clear') {
      contentsRef.current.contents = DEFAULT_VALUE;
      setIsClearing(false); 
      setEditable(false);
      setTextEditable(false);
      return;
    }
    contentsRef.current.type = blockDetails?.type ? blockDetails.type : undefined;
    props.onSaveChanges(contentsRef.current);
    const autosaveCallback = () => {
      type === 'clear' ? setIsClearing(false) : setIsSaving(false);
      setEditable(false);
      setTextEditable(false);
    }
    store.set({ [COMPLEX_DOCUMENT_CONTEXT_KEYS.AUTOSAVE_CALLBACK]: autosaveCallback });
  }, [blockDetails?.type]);

    return (
      <div style={{ position: itemUUID.includes('accessibility-summary') ? 'absolute' : 'relative', width: itemUUID.includes('accessibility-summary') ? '92%' : '100%', ...customContainerStyle }} className={disabled && 'disabled-editor'}>
        <div id={`summary-toolbar-${itemUUID}`} style={{ visibility: "hidden", height: 0 }} />
        <div id={`summary-variable-${itemUUID}`} style={{ position: 'absolute', top: 10 }} />
        <TextBlockWithQuillRenderer
          defaultContent={defaultContent}
          saveBlock={updateSummaryContents}
          itemUUID={itemUUID}
          selected={editable}
          enableSideBar={dummyCallback}
          enableAI={false}
          toolbarInvisible={true}
          adjustScroll={dummyCallback}
          toolbarDOM={`summary-toolbar-${itemUUID}`}
          variableDOM={`summary-variable-${itemUUID}`}
          textEditable={textEditable}
          setTextEditable={setTextEditable}
          customClassName={customClassName}
          getEditorContentLength={dummyCallback}
          backgroundColor={'white'}
          isMailSubject={true}
          onContainerSingleClick={onSingleClick}
          customizeHistory={false}
          border_radius={'4px'}
          // customBlurHandler={customBlurHandler}
        >
          {!editable && !textEditable && convertContractToReact(contents || [], documentStyle?.fontFamily, documentStyle?.fontSize, globalVariables)}
        </TextBlockWithQuillRenderer>
        <div className={'summary-button-parent'}>
          {(itemUUID.includes("element-summary") || itemUUID.includes("block-summary")) && !itemUUID.includes('alttext') && editable && !disabled &&
            <div className={'summary-clear-button'} onClick={() => { onSaveChanges('clear') }}>
              {isClearing ? <> <Icon type="loading" /> CLEAR </> : <> <Icon type="delete" /> CLEAR </>}
            </div>
          }
          {editable && !disabled && !(itemUUID.includes("document-summary") || itemUUID.includes("accessibility-summary")) && (
            <div className={'summary-save-button'} onClick={() => { onSaveChanges('save') }}>
              {isSaving ? <> <Icon type="loading" /> SAVE </> : <> <Icon type="save" /> SAVE </>}
            </div>
          )}
          {editable && !disabled && (itemUUID.includes("document-summary") || itemUUID.includes("email-summary") || itemUUID.includes("accessibility-summary")) && (
            <div className={'summary-save-button'} style={{ fontWeight: '100' }} onClick={() => { onSaveChanges('save') }}>
              {isSaving ? <> <Icon type="loading" /> Save </> : <> Save </>}
            </div>
          )}
        </div>

      </div>
    );
}

export default withTranslation(NAMESPACE)(AccessibilitySummaryEditor);